import { useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { Trans, useTranslation } from "@/translations";

const FORM_ID = "close-project-modal-form";

type CloseProjectModal = ModalBaseProps & {
  projectName: string;
  isLoading: boolean;
  onSubmit: (deleteUsers: boolean) => void;
};

export function CloseProjectModal({
  isOpened,
  onOpenChange,
  projectName,
  onSubmit,
  isLoading,
}: CloseProjectModal): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<CloseProjectModalValues>({
    defaultValues: {
      name: "",
      deleteUsers: true,
    },
  });

  const deleteUsers = useWatch({ control: form.control, name: "deleteUsers" });

  return (
    <Modal.Root
      title={t("page.projects.close-project.modal.title")}
      description={
        <Trans
          i18nKey="page.projects.close-project.modal.description"
          components={{
            b: <strong />,
          }}
        />
      }
      size="sm"
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root<CloseProjectModalValues>
          id={FORM_ID}
          formMethods={form}
          onSubmit={(data) => onSubmit(data.deleteUsers)}
          noPrompt
        >
          <Stack gap="md">
            <FormField label="">
              <FormInput<CloseProjectModalValues>
                id="name"
                name="name"
                placeholder={projectName}
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.projects.close-project.modal.form.name"),
                    notSameAsProjectName: (value) => {
                      if (!value) {
                        return undefined;
                      }

                      return value === projectName
                        ? undefined
                        : t("page.projects.close-project.modal.error.not-same-as-name");
                    },
                  },
                }}
              />
            </FormField>
            <FormField label="">
              <FormCheckbox<CloseProjectModalValues>
                name="deleteUsers"
                label={t("page.projects.close-project.modal.form.delete-users")}
              />

              {deleteUsers && (
                <aside className="mt-space-sm">
                  <Notice
                    variant="warning"
                    icon={<AlertTriangleIcon />}
                    message={t("page.projects.close-project.modal.warning")}
                  />
                </aside>
              )}
            </FormField>
          </Stack>
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        <Modal.Close>
          <Button type="button" variant="outline" tone="primary" disabled={isLoading}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button type="submit" variant="filled" tone="warning" form={FORM_ID} isLoading={isLoading}>
          {t("page.projects.close-project.modal.confirm")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}

interface CloseProjectModalValues {
  name: string;
  deleteUsers: boolean;
}
